// @givmo/compliance — the signer's page: a request someone was asked to sign (CCM-R1-SIGN-WEB;
// slice 2 §6(b), frames 38, 38-390 and 40, approved as shown, D-CCM-22).
//
// `/compliance/sign/<request id>`, the address the backend's invitation email links to. The person
// sees the exact pages they sign (the request's own rendition set), types their name and sees it in
// the approved script font at the form's own signature place, reads the statement a signature
// affirms, and signs by ONE deliberate act. At the organization's default level there is no prompt;
// at a stricter one the act runs through the ONE "Confirm it's you" (D-SIGN-1;
// `compliance-signing-gate.tsx`). Decline is always there and is not final (design §2.5).
//
// ══ WHAT THE ACT SENDS ═════════════════════════════════════════════════════════════════════════
//
// Exactly what the page was given: the live version's id, manifest SHA-256, package digest and
// rendition set, the statement's id, and the typed name. The echoes are never recomputed here. Any
// act the server refuses with a 409 — a version that changed underneath, a request that closed or
// expired, an act another tab took first — reads the request again, so the page around the served
// line is the request as it is now, never an act it no longer offers; where that request has
// closed, its closed card says so in place of the line (CCM-R1-SIGN-WEB-C, cut 1006-7's N2).
//
// ══ ONE PERSON, ONE VIEW ═══════════════════════════════════════════════════════════════════════
//
// The page speaks to the caller: their places (`my_slots`), what they may do now (`can_*`), their
// own acts. Another signer's standing is the sender's view (the workbench, CCM-R1-SIGN-WEB-B). The
// read answers every Compliance reader, with `my_slots: null` for one who holds no place on it (a
// forwarded email, the sender, a replacement that names someone else): this page then says what it
// says for a request that doesn't exist, the built not-found, never "asked you to sign". The act
// itself is the server's not-found for them.
//
// ══ A REQUEST NO ONE CAN SIGN NOW ══════════════════════════════════════════════════════════════
//
// A request that ended says so in the panel's place: cancelled or expired, the approved status
// lines' first sentences. A request whose filing case was withdrawn stays open, and every act on it
// is refused; the read serves the refusal a signer's act meets now, and the page shows that line,
// as served, in the same place (CCM-R1-SIGN-WEB-WITHDRAWN), never a Sign that is silently off.
// The request's own end comes first, and a person who signed keeps their signed card.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorMessage,
  Field,
  Icon,
  Skeleton,
  Textarea,
  testIdProp,
} from '@givmo/design-system';
import { Building2, Download } from 'lucide-react';
import { StepUpPasskeyFailure, isStepUpCancelled, useOptionalStepUpHost } from '@givmo/step-up';
import { useCompliance } from './compliance-context';
import { isActConflict, serverMessage } from './compliance-decode';
import { ComplianceDisclosure } from './compliance-disclosure';
import { COMPLIANCE_PAGE_TEST_IDS, COMPLIANCE_ROUTES } from './compliance-routes';
import { STICKY_COLUMN_CLASSES } from './compliance-shared';
import { useComplianceActGate } from './compliance-signing-gate';
import {
  SIGNING_TEXT_LIMITS,
  decodeSigningRequestDetail,
  decodeSigningSetting,
  type SigningRequestDetail,
} from './compliance-signing-decode';
import {
  DATE_LABEL,
  DECLINE,
  DECLINE_TITLE,
  DOWNLOAD_THE_SIGNED_FORM,
  KEEP_IT,
  NOT_FOUND_LINE,
  NOT_FOUND_TITLE,
  LOAD_FAILED,
  REASON_OPTIONAL,
  SIGN,
  SIGNED_FORM_READY,
  TITLE_LABEL,
  TYPE_YOUR_FULL_NAME,
  WITHDRAW_MY_SIGNATURE,
  YOUR_SIGNATURE,
  asStatedLine,
  cannotMeetCheckLine,
  closedLine,
  dayYouSignLine,
  doesntSendLine,
  documentOf,
  firstPlace,
  howItLooksLine,
  iHaveSigned,
  myLatestAct,
  myPlaces,
  statementLine,
  toldWithReasonLine,
  willBeToldLine,
  youDeclinedLine,
  youSignedLine,
} from './compliance-signing-model';
import { signingRequestQueryKey, signingSettingQueryKey } from './compliance-queries';
import {
  SigningAttachments,
  SigningHeader,
  SigningPageColumn,
  TypedNamePreview,
  useNameWidthAtOnePoint,
  useOpenVersion,
  useSignatureFontReady,
  useSignedFinals,
  useSigningReads,
} from './signing-shared';

const PREFIX = 'compliance-signing';

/** Built: an act that failed with no words of its own. */
const RECORD_FAILED = 'We couldn’t record that. Please try again.';

/** What a failed act shows: the server's own words where it gave some. */
function actFailure(error: unknown): string | undefined {
  if (error === null || error === undefined || isStepUpCancelled(error)) return undefined;
  // No passkey to answer a passkey-only prompt: the page offers one instead (S10), no error line.
  if (noPasskey(error)) return undefined;
  return serverMessage(error, RECORD_FAILED);
}

/** The gate failed the act because only a passkey could answer and the account holds none. */
function noPasskey(error: unknown): boolean {
  return error instanceof StepUpPasskeyFailure && error.reason === 'none_registered';
}

/** The page's wrapper: the layout every state shares. */
function SigningFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex w-full max-w-[1120px] flex-col gap-24 px-16 py-28 sm:px-24 lg:px-32"
      {...testIdProp(COMPLIANCE_PAGE_TEST_IDS.signing)}
    >
      {children}
    </div>
  );
}

export function SigningPage() {
  const { isError: orgError, error: orgFailure } = useCompliance();
  const { requestId = '' } = useParams({ strict: false }) as { requestId?: string };
  const reads = useSigningReads(requestId);
  const navigate = useNavigate();
  const supersededBy =
    reads.detail?.request.state === 'superseded'
      ? reads.detail.request.supersededByRequestId
      : undefined;

  // A request replaced by a newer one: the newer one is the one to sign (its own invitation says so).
  useEffect(() => {
    if (supersededBy !== undefined) {
      void navigate({ to: COMPLIANCE_ROUTES.signing(supersededBy), replace: true });
    }
  }, [supersededBy, navigate]);

  if (orgError) {
    return (
      <SigningFrame>
        <EmptyState
          testId={`${PREFIX}-org-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={serverMessage(orgFailure, 'Please try again.')}
        />
      </SigningFrame>
    );
  }
  // A read whose request holds no place of the caller's is not theirs to sign: the not-found (F4 of
  // cut 1003-1's review). A replaced request is followed first, whatever it names.
  const notTheirs =
    reads.detail !== undefined &&
    supersededBy === undefined &&
    !reads.pending &&
    reads.detail.mySlots.slotIds.length === 0;
  if (
    reads.notFound ||
    notTheirs ||
    (reads.failed === undefined && !reads.pending && reads.detail === undefined)
  ) {
    return (
      <SigningFrame>
        <EmptyState
          testId={`${PREFIX}-not-found`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title={NOT_FOUND_TITLE}
          description={NOT_FOUND_LINE}
        />
      </SigningFrame>
    );
  }
  if (reads.failed !== undefined) {
    return (
      <SigningFrame>
        <p className="text-s14 text-text-secondary" {...testIdProp(`${PREFIX}-error`)}>
          {serverMessage(reads.failed, LOAD_FAILED)}
        </p>
      </SigningFrame>
    );
  }
  if (reads.detail === undefined || reads.pending || supersededBy !== undefined) {
    return (
      <SigningFrame>
        <Skeleton className="h-[480px] w-full" {...testIdProp(`${PREFIX}-loading`)} />
      </SigningFrame>
    );
  }
  return <SigningRequest requestId={requestId} detail={reads.detail} reads={reads} />;
}

function SigningRequest({
  requestId,
  detail,
  reads,
}: {
  requestId: string;
  detail: SigningRequestDetail;
  reads: ReturnType<typeof useSigningReads>;
}) {
  const { orgRef, operations, organizationName, personName, renderPasskeySetupOffer } =
    useCompliance();
  const queryClient = useQueryClient();
  const gate = useComplianceActGate();
  const host = useOptionalStepUpHost();
  const fontReady = useSignatureFontReady();
  const [typedName, setTypedName] = useState('');
  const [declining, setDeclining] = useState(false);
  // S10's offer: opened from Sign; once a passkey exists the act runs again through the gate, with
  // exactly the name Sign was pressed with (`pressedName`). The name is read-only while the offer
  // stands, and any change of it afterwards disarms the re-run: only a press sends a name (F1 of
  // cut 1003-1's review).
  const [offerOpen, setOfferOpen] = useState(false);
  const [pressedName, setPressedName] = useState<string | null>(null);
  const [signAfterSetup, setSignAfterSetup] = useState(false);
  // The offer settled `unavailable`: this browser or this environment can't create a passkey, so
  // Sign is shut as when there is no offer at all, rather than reopening an offer that can't work.
  const [offerUnavailable, setOfferUnavailable] = useState(false);
  const [reason, setReason] = useState('');
  const name = typedName.trim();
  const widthAtOnePoint = useNameWidthAtOnePoint(name, fontReady);
  const zone = detail.version.timeZone;
  const place = firstPlace(detail);
  const document = documentOf(detail, place);
  const placePage = place?.page ?? document?.pages[0]?.page ?? 1;
  const signedByMe = iHaveSigned(detail);
  const completed = detail.request.state === 'completed';
  const closed = closedLine(detail, personName);
  // The served refusal, where the page would otherwise ask this person to sign.
  const refused = !signedByMe && closed === undefined ? detail.refusal : undefined;
  // Whether the page still asks this person to sign: the sign-by date and the name drawn on the form.
  const asking = !signedByMe && closed === undefined && refused === undefined;
  const declined = myPlaces(detail).some((slot) => slot.standing === 'declined')
    ? myLatestAct(detail, 'decline')
    : undefined;
  const mySignature = myLatestAct(detail, 'sign');
  const finals = useSignedFinals(detail).filter((entry) => entry.final !== undefined);
  const openVersion = useOpenVersion();
  const setting = useQuery({
    queryKey: signingSettingQueryKey(orgRef),
    queryFn: () => operations.getSigningSetting(orgRef ?? ''),
    enabled: orgRef !== null,
  });
  const level = decodeSigningSetting(setting.data)?.level;
  const strict = level !== undefined && level !== 'signed_in';
  // At a stricter level, what the account holds decides between Sign and the setup offer. A host
  // that reads that only on demand (`prepare`, AS-WEB15) reads it here, once; until it has answered,
  // and while a read is on the wire (`preparing`), the page doesn't know yet and offers nothing in
  // place of Sign (the server decides a press meanwhile).
  const prepare = host?.prepare;
  const [prepared, setPrepared] = useState(false);
  useEffect(() => {
    if (!strict || prepare === undefined) return;
    let live = true;
    const done = () => {
      if (live) setPrepared(true);
    };
    void prepare().then(done, done);
    return () => {
      live = false;
    };
  }, [strict, prepare]);
  const known = prepare === undefined || (prepared && host?.preparing !== true);
  // D-AS-24/25/26: a stricter level this account has no way in the prompt accepts to meet here.
  const cannotMeetCheck =
    strict &&
    host !== null &&
    known &&
    host.waysIn.length === 0 &&
    host.passkey?.holdsPasskey !== true;
  const factual = detail.documents.find(
    (candidate) => candidate.support.factualLineApplies && candidate.support.factualLine,
  )?.support.factualLine;
  const echoes = {
    request_version_id: detail.version.requestVersionId,
    manifest_sha256: detail.version.manifestSha256,
    package_digest: detail.version.packageDigest,
  };

  /** A changed request reads again; the act's own answer replaces the read. */
  const settle = (answer: unknown) => {
    const key = signingRequestQueryKey(orgRef, requestId);
    if (decodeSigningRequestDetail(answer) !== undefined) {
      queryClient.setQueryData(key, answer);
    } else {
      void queryClient.invalidateQueries({ queryKey: key });
    }
    // The workbench's lock and the panel's Signed line read the same family.
    void queryClient.invalidateQueries({
      queryKey: ['compliance', 'case-signing-requests', orgRef],
    });
  };
  const rereadOnConflict = (error: unknown) => {
    if (isActConflict(error)) {
      void queryClient.invalidateQueries({ queryKey: signingRequestQueryKey(orgRef, requestId) });
    }
  };

  // The act sends the name it is given: the one the person pressed Sign with.
  const sign = useMutation({
    mutationFn: (pressed: string) =>
      gate.run(() =>
        operations.signSigningRequest(orgRef ?? '', requestId, {
          ...echoes,
          rendition_set_id: detail.version.renditionSetId,
          intent_statement_id: detail.intent.statementId,
          typed_name: pressed,
        }),
      ),
    onSuccess: settle,
    onError: (error, pressed) => {
      rereadOnConflict(error);
      if (noPasskey(error) && renderPasskeySetupOffer !== undefined) {
        setPressedName(pressed);
        setOfferOpen(true);
      }
    },
  });
  const decline = useMutation({
    mutationFn: () =>
      operations.declineSigningRequest(orgRef ?? '', requestId, {
        ...echoes,
        reason: reason.trim().length > 0 ? reason.trim() : null,
      }),
    onSuccess: (answer) => {
      settle(answer);
      setDeclining(false);
      setReason('');
    },
    onError: rereadOnConflict,
  });
  const withdraw = useMutation({
    mutationFn: () => gate.run(() => operations.withdrawSignature(orgRef ?? '', requestId, echoes)),
    onSuccess: settle,
    onError: rereadOnConflict,
  });

  const failure = actFailure(sign.error) ?? actFailure(withdraw.error);
  const canSign = detail.mySlots.canSign;
  const holdsPasskey = host?.passkey?.holdsPasskey === true;
  const signMutate = sign.mutate;
  const signing = sign.isPending;
  // The passkey just set up is the account's once the host reads it: then the act runs again, with
  // the name Sign was pressed with. A name changed since that press (or none) disarms it instead.
  useEffect(() => {
    if (!signAfterSetup) return;
    if (pressedName === null || pressedName.length === 0 || name !== pressedName) {
      setSignAfterSetup(false);
      setPressedName(null);
      return;
    }
    if (!holdsPasskey || signing) return;
    setSignAfterSetup(false);
    setPressedName(null);
    signMutate(pressedName);
  }, [signAfterSetup, pressedName, holdsPasskey, signing, name, signMutate]);
  const onSign = () => {
    // S10: nothing on this account can meet the check here, so Sign opens the offer, sending nothing.
    if (cannotMeetCheck && renderPasskeySetupOffer !== undefined) {
      setPressedName(name);
      setOfferOpen(true);
      return;
    }
    sign.mutate(name);
  };
  // No line where the filing's jurisdiction isn't known here: the approved line names it.
  const doesntSend = doesntSendLine(reads.jurisdiction);

  const panel = signedByMe ? (
    <Card className="flex flex-col gap-14 px-20 py-20" testId={`${PREFIX}-signed`}>
      <h2 className="text-s16 font-w600 text-text-strong">
        {youSignedLine(mySignature?.occurredAt ?? place?.standingActAt, zone)}
      </h2>
      {completed && (
        <>
          <p className="text-s13 leading-h15 text-text">{SIGNED_FORM_READY}</p>
          {finals.length === 1 && finals[0] !== undefined && (
            <Button
              appearance="saas"
              tone="secondary"
              size="md"
              className="self-start"
              leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
              onClick={() =>
                void openVersion(finals[0]?.documentId ?? '', finals[0]?.final?.versionId ?? '')
              }
              testId={`${PREFIX}-download-signed`}
            >
              {DOWNLOAD_THE_SIGNED_FORM}
            </Button>
          )}
          {/* A package of several documents: each signed form, under its form number. */}
          {finals.length > 1 &&
            finals.map((entry) => {
              const adapterKey = detail.documents.find(
                (candidate) => candidate.documentId === entry.documentId,
              )?.adapterKey;
              const form = adapterKey !== undefined ? reads.formNumberOf(adapterKey) : undefined;
              return (
                <div
                  key={entry.documentId}
                  className="flex flex-col gap-6"
                  {...testIdProp(`${PREFIX}-signed-document`)}
                >
                  {form !== undefined && <p className="text-s13 text-text-secondary">{form}</p>}
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    className="self-start"
                    leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                    onClick={() => void openVersion(entry.documentId, entry.final?.versionId ?? '')}
                    testId={`${PREFIX}-download-signed`}
                  >
                    {DOWNLOAD_THE_SIGNED_FORM}
                  </Button>
                </div>
              );
            })}
          {/* Q7 (approved, D-CCM-29): "will be told", since the notice leaves on the next pass. */}
          <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-told`)}>
            {willBeToldLine(reads.sender)}
          </p>
        </>
      )}
      {detail.mySlots.canWithdraw && (
        <button
          type="button"
          className="self-start text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong"
          onClick={() => withdraw.mutate()}
          disabled={withdraw.isPending}
          {...testIdProp(`${PREFIX}-withdraw`)}
        >
          {WITHDRAW_MY_SIGNATURE}
        </button>
      )}
      {failure !== undefined && <ErrorMessage testId={`${PREFIX}-failure`}>{failure}</ErrorMessage>}
      {doesntSend !== undefined && (
        <p className="border-t border-border-subtle pt-12 text-s13 leading-h15 text-text-secondary">
          {doesntSend}
        </p>
      )}
    </Card>
  ) : closed !== undefined ? (
    <Card className="flex flex-col gap-12 px-20 py-20" testId={`${PREFIX}-closed`}>
      <p className="text-s13 leading-h15 text-text">{closed}</p>
    </Card>
  ) : refused !== undefined ? (
    <Card className="flex flex-col gap-12 px-20 py-20" testId={`${PREFIX}-refused`}>
      <p className="text-s13 leading-h15 text-text">{refused.detail}</p>
    </Card>
  ) : (
    <Card className="flex flex-col gap-16 px-20 py-20" testId={`${PREFIX}-panel`}>
      {declined !== undefined && (
        // A reason keeps its line breaks (D-CCM-35 item 5 (C)).
        <p
          className="whitespace-pre-line text-s13 leading-h15 text-text"
          {...testIdProp(`${PREFIX}-declined`)}
        >
          {youDeclinedLine(declined.occurredAt, declined.reason, zone)}
        </p>
      )}
      <h2 className="text-s16 font-w600 text-text-strong">{YOUR_SIGNATURE}</h2>
      <Field
        label={TYPE_YOUR_FULL_NAME}
        value={typedName}
        autoComplete="name"
        maxLength={SIGNING_TEXT_LIMITS.typedName}
        onChange={(event) => setTypedName(event.target.value)}
        disabled={!canSign}
        // The name Sign was pressed with stands while the passkey offer is open (F1).
        readOnly={offerOpen}
        testId={`${PREFIX}-typed-name`}
      />
      <div className="flex flex-col gap-6">
        <p className="text-s13 font-w500 text-text-secondary">{howItLooksLine(placePage)}</p>
        <TypedNamePreview name={name} fontReady={fontReady} />
      </div>
      {place !== undefined && (
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-20 gap-y-8 text-s13">
          <dt className="text-text-tertiary">{TITLE_LABEL}</dt>
          <dd className="text-text" {...testIdProp(`${PREFIX}-title`)}>
            {asStatedLine(place.capacityText, organizationName)}
          </dd>
          <dt className="text-text-tertiary">{DATE_LABEL}</dt>
          <dd className="text-text">{dayYouSignLine(new Date(), zone)}</dd>
        </dl>
      )}
      <div className="flex flex-col gap-8 border-t border-border-subtle pt-16 text-s13 leading-h15 text-text">
        <p>{statementLine(placePage)}</p>
        {factual !== undefined && <p {...testIdProp(`${PREFIX}-factual`)}>{factual}</p>}
        {doesntSend !== undefined && <p>{doesntSend}</p>}
        <ComplianceDisclosure surface="signer-page" />
      </div>
      {cannotMeetCheck && (
        // S10 (approved, D-CCM-29; D-AS-24/25/26).
        <p
          className="text-s13 leading-h15 text-text"
          {...testIdProp(`${PREFIX}-cannot-meet-check`)}
        >
          {cannotMeetCheckLine(organizationName)}
        </p>
      )}
      <div className="flex flex-col gap-10">
        <p
          className="text-s14 font-w600 leading-h15 text-text-strong"
          {...testIdProp(`${PREFIX}-intent`)}
        >
          {detail.intent.text}
        </p>
        {offerOpen && renderPasskeySetupOffer !== undefined ? (
          // S10: the host's offer, in place of Sign until it settles (its "Not now" brings Sign back;
          // an offer that can't work here brings it back shut).
          <div {...testIdProp(`${PREFIX}-passkey-offer`)}>
            {renderPasskeySetupOffer({
              act: 'sign',
              onSettled: (outcome) => {
                setOfferOpen(false);
                if (outcome === 'created') {
                  setSignAfterSetup(true);
                } else {
                  setPressedName(null);
                  if (outcome === 'unavailable') setOfferUnavailable(true);
                }
              },
            })}
          </div>
        ) : (
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            disabled={
              !canSign ||
              name.length === 0 ||
              (cannotMeetCheck && (renderPasskeySetupOffer === undefined || offerUnavailable))
            }
            loading={gate.inFlight}
            onClick={onSign}
            testId={`${PREFIX}-sign`}
          >
            {SIGN}
          </Button>
        )}
        {failure !== undefined && (
          <ErrorMessage testId={`${PREFIX}-failure`}>{failure}</ErrorMessage>
        )}
        {/* Signing is always the person pressing Sign here: no AI signs (D-CCM-31). */}
        {detail.mySlots.canDecline && (
          <div className="flex flex-wrap items-center gap-12">
            <button
              type="button"
              className="text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong"
              onClick={() => setDeclining(true)}
              {...testIdProp(`${PREFIX}-decline`)}
            >
              {DECLINE}
            </button>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <SigningFrame>
      <SigningHeader reads={reads} showSignBy={asking} />
      <div className="grid items-start gap-32 lg:grid-cols-[600px_minmax(0,1fr)]">
        <SigningPageColumn
          detail={detail}
          typedName={name}
          fontReady={fontReady}
          widthAtOnePoint={widthAtOnePoint}
          drawName={asking}
          formNumberOf={reads.formNumberOf}
        />
        <div className={`flex min-w-0 flex-col gap-24 ${STICKY_COLUMN_CLASSES}`}>
          {panel}
          <SigningAttachments items={reads.checklist} />
        </div>
      </div>
      {gate.dialog}
      <Dialog.Root
        open={declining}
        onOpenChange={(open) => {
          if (!open && !decline.isPending) setDeclining(false);
        }}
      >
        {declining && (
          <Dialog.Content
            width={440}
            className="flex flex-col gap-16"
            testId={`${PREFIX}-decline-dialog`}
          >
            <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              {DECLINE_TITLE}
            </Dialog.Title>
            <Dialog.Description className="text-s14 text-text-secondary">
              {toldWithReasonLine(reads.sender)}
            </Dialog.Description>
            <Textarea
              label={REASON_OPTIONAL}
              value={reason}
              maxLength={SIGNING_TEXT_LIMITS.reason}
              onChange={(event) => setReason(event.target.value)}
              testId={`${PREFIX}-decline-reason`}
            />
            {decline.error !== null && decline.error !== undefined && (
              <ErrorMessage testId={`${PREFIX}-decline-failure`}>
                {serverMessage(decline.error, RECORD_FAILED)}
              </ErrorMessage>
            )}
            <div className="flex justify-end gap-10">
              <Button
                appearance="saas"
                tone="secondary"
                type="button"
                onClick={() => setDeclining(false)}
                disabled={decline.isPending}
                testId={`${PREFIX}-decline-keep`}
              >
                {KEEP_IT}
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                type="button"
                loading={decline.isPending}
                // The request as read again after a refusal: no Decline where it no longer can.
                disabled={!detail.mySlots.canDecline}
                onClick={() => decline.mutate()}
                testId={`${PREFIX}-decline-confirm`}
              >
                {DECLINE}
              </Button>
            </div>
          </Dialog.Content>
        )}
      </Dialog.Root>
    </SigningFrame>
  );
}
